/**
 * Color Constants
 * Centralized color definitions for consistent styling
 */
import { SCORE_SCALE_GOOD, SCORE_SCALE_MID, SCORE_SCALE_BAD } from './scoreScale';

// Primary application colors
export const PRIMARY_COLORS = {
  primary: '#A1E430',
  primaryLight: '#B8EC5A',
  primaryDark: '#8BC92A',
  secondary: '#8B7FD9',
  secondaryLight: '#A89BE5',
  secondaryDark: '#7568C7',
  accent: '#1890ff',
  accentHover: '#40a9ff',
  brandPurple: '#8C74FF',
};

// Chart-specific colors
export const CHART_COLORS = {
  primary: '#A1E430',
  secondary: '#FF8C42',
  tertiary: '#8B7FD9',
  quaternary: '#FFE100',
  velocity: '#FF4D4D',
  congestion: '#FF852B',
  anomaly: '#A1E430',
  audio: '#FF8C42',
  vibration: '#8B7FD9',
  environment: '#38A3A5',
  flow: '#52c41a',
  travelTime: '#FFE100',
};

// Road selection colors (for multi-road views)
export const ROAD_COLORS = ['#A1E430', '#8B7FD9', '#FF8C42', '#FFE100'];

// Network view color scales (workbook scale: scala_rgb_1_10.xlsx)
const SCALE_GOOD = SCORE_SCALE_GOOD;
const SCALE_MID = SCORE_SCALE_MID;
const SCALE_BAD = SCORE_SCALE_BAD;

export const NETWORK_VIEW_COLORS = {
  velocita: {
    high: SCALE_GOOD,     // > 40 km/h
    medium: SCALE_MID,    // 20-40 km/h
    low: SCALE_BAD,       // < 20 km/h
    noData: '#4A5568',
  },
  flow: {
    high: SCALE_BAD,      // > 800 vehicles (congested)
    medium: SCALE_MID,    // 400-800 vehicles
    low: SCALE_GOOD,      // < 400 vehicles (free flow)
    noData: '#4A5568',
  },
  travelTime: {
    high: SCALE_BAD,      // > 300 seconds (slow)
    medium: SCALE_MID,    // 100-300 seconds
    low: SCALE_GOOD,      // < 100 seconds (fast)
    noData: '#4A5568',
  },
};

// Legend items for network views
export const LEGEND_ITEMS = {
  velocita: [
    { color: SCALE_GOOD, label: '> 40 km/h' },
    { color: SCALE_MID, label: '20-40 km/h' },
    { color: SCALE_BAD, label: '< 20 km/h' },
    { color: '#4A5568', label: 'Nessun dato' },
  ],
  flow: [
    { color: SCALE_GOOD, label: '< 400 VEH' },
    { color: SCALE_MID, label: '400-800 VEH' },
    { color: SCALE_BAD, label: '> 800 VEH' },
    { color: '#4A5568', label: 'Nessun dato' },
  ],
  travelTime: [
    { color: SCALE_GOOD, label: '< 100 s' },
    { color: SCALE_MID, label: '100-300 s' },
    { color: SCALE_BAD, label: '> 300 s' },
    { color: '#4A5568', label: 'Nessun dato' },
  ],
};

// Semantic colors
export const SEMANTIC_COLORS = {
  success: '#52c41a',
  warning: '#faad14',
  danger: '#ff4d4f',
  info: '#1890ff',
};

// Text colors
export const TEXT_COLORS = {
  primary: '#333333',
  secondary: '#666666',
  muted: '#999999',
  disabled: '#bfbfbf',
  inverse: '#ffffff',
};

// Background colors
export const BG_COLORS = {
  primary: '#ffffff',
  secondary: '#fafafa',
  tertiary: '#f5f5f5',
  dark: '#353A0A',
  overlay: 'rgba(0, 0, 0, 0.45)',
};

// Border colors
export const BORDER_COLORS = {
  default: '#e0e0e0',
  light: '#f0f0f0',
  dark: '#d9d9d9',
};

/**
 * Get color for velocity value
 * @param {number} speed - Speed in km/h
 * @returns {string} Color hex code
 */
export const getVelocityColor = (speed) => {
  if (!speed || speed <= 0) return NETWORK_VIEW_COLORS.velocita.noData;
  if (speed > 40) return NETWORK_VIEW_COLORS.velocita.high;
  if (speed >= 20) return NETWORK_VIEW_COLORS.velocita.medium;
  return NETWORK_VIEW_COLORS.velocita.low;
};

/**
 * Get color for flow value
 * @param {number} flow - Flow in vehicles per 15 min
 * @returns {string} Color hex code
 */
export const getFlowColor = (flow) => {
  if (!flow || flow < 0) return NETWORK_VIEW_COLORS.flow.noData;
  if (flow > 800) return NETWORK_VIEW_COLORS.flow.high;
  if (flow >= 400) return NETWORK_VIEW_COLORS.flow.medium;
  return NETWORK_VIEW_COLORS.flow.low;
};

/**
 * Get color for travel time value
 * @param {number} time - Travel time in seconds
 * @returns {string} Color hex code
 */
export const getTravelTimeColor = (time) => {
  if (!time || time <= 0) return NETWORK_VIEW_COLORS.travelTime.noData;
  if (time > 300) return NETWORK_VIEW_COLORS.travelTime.high;
  if (time >= 100) return NETWORK_VIEW_COLORS.travelTime.medium;
  return NETWORK_VIEW_COLORS.travelTime.low;
};

/**
 * Get color for road index (multi-road selection)
 * @param {number} index - Road index (0-based)
 * @returns {string} Color hex code
 */
export const getRoadColor = (index) => {
  return ROAD_COLORS[index % ROAD_COLORS.length];
};
